import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { SimpleCodeValue } from "../models/code-value.model";
import { GenericApiResponse } from "../models/api-response.model";

@Injectable({
    providedIn: 'root'
})
export class ManageComplaintService {

    constructor(private http: HttpClient) { }

    getMyZoneDepartments(): Observable<SimpleCodeValue[]> {
        return this.http.get<SimpleCodeValue[]>('/department/v1/get-departments/code-values');
    }

    getMyZoneAssignableDepartments(): Observable<SimpleCodeValue[]> {
        return this.http.get<SimpleCodeValue[]>('/department/v1/get-assignable-departments/code-values');
    }

    getEmployeesWithAssignableComplaints(): Observable<SimpleCodeValue[]> {
        return this.http.get<SimpleCodeValue[]>('/employee/v1/complaint-assignable-employees');
    }

    getAccessibleDepartments(): Observable<SimpleCodeValue[]> {
        return this.http.get<SimpleCodeValue[]>('/department/v1/get-accessible-departments');
    }

    getJobCardSearchDetails(body: JobCardSearchRequest): Observable<JobCardDetails[]> {
        return this.http.post<JobCardDetails[]>('/job-card/v1/get-job-cards', body);
    }

    resentOtpForJobClose(body: JobCardDetails): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>('/job-card/v1/resend-job-close-otp', body);
    }

    getEmployeesAssignableForDepartment(departmentId: string): Observable<SimpleCodeValue[]> {
        return this.http.get<SimpleCodeValue[]>('/employee/v1/department-assignable-employees/{departmentId}'.replace('{departmentId}', departmentId));
    }

    getDepartmentsWithSameParentDepartment(departmentId: string): Observable<SimpleCodeValue[]> {
        return this.http.get<SimpleCodeValue[]>('/department/v1/get-same-parent-departments/{departmentId}'.replace('{departmentId}', departmentId));
    }

    saveJobCard(body: CreateJobCardVoRequest): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>('/job-card/v1/create-job-card', body);
    }

    transferComplaint(body: { complaintLineIds: number[]; departmentId: number; }): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>('/complaints/v1/transfer-complaint', body);
    }

    getComplaintHistory(complaintLineId: number): Observable<ComplaintTaskHistVo> {
        return this.http.get<ComplaintTaskHistVo>('/complaints/v1/complaint-history/{complaintLineId}'.replace('{complaintLineId}', complaintLineId.toString()));
    }

    updateComplaintStatus(body: UpdateComplaintStatusVo): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>('/complaints/v1/update-complaint-status', body);
    }


}

export interface UpdateComplaintStatusVo {
    jobCardSearchResultList: JobCardDetails[];
    jobCardComments: string;
    additionalComment: string;
    newStatus: string;
}

export interface CreateJobCardVoRequest {
    jobCardSearchResultList: JobCardDetails[];
    employeeIdForIssue: number;
    departmentIdForIssue: number;
    jobCardComments: string;
}

export interface JobCardSearchRequest {
    jobCardStatus: string[] | null | undefined;
    complaintStatus: string[] | null | undefined;
    limit: number;
    offset: number;
    departmentId: number | null | undefined;
    employeeId: number | null | undefined;
    areaId: number | null | undefined;
    complaintNumber: string | null | undefined;
    jobCardNumber: string | null | undefined;
}

export interface JobCardDetails {
    complaintId: number;
    masterComplaintId: number;
    complaintDescription: string;
    allocationId: number | null;
    locationId: number;
    complaintDate: string;
    complaintMode: string;
    complaintNotes: string;
    severity: number;
    employeeId: number | null;
    residentId: number;
    complaintCode: string | null;
    categoryId: number;
    smsMessage: string | null;
    complaintStatus: string;
    dueDate: string;
    areaId: number;
    complaintAssignmentId: number;
    compAssignedEmpId: number;
    compAssignedDeptId: number;
    complaintAssignComments: string;
    compAssStartDate: string;
    compAssEndDate: string | null;
    taskId: number;
    docketNo: string;
    taskDeptId: number;
    taskAssEmpId: number;
    taskAssignedByEmpId: number;
    taskStatus: string;
    taskNotes: string;
    locationCode: string;
    areaName: string;
    residentFirstName: string;
    residentLastName: string;
    residentMobileNo: string;
    compEmpFName: string;
    compEmpLName: string;
    compMobileNo: string;
    taskEmpFName: string;
    taskEmpLName: string;
    taskEmpMobileNo: string;
    complDepName: string | null;
    taskDeptName: string;
    compCatName: string | null;
    complaintStatusMeaning: string | null;
    taskStatusMeaning: string | null;
    locationName: string | null;
    taskCreationDate: string;
    notes: string;
    taskUpdateDate: string;
    rowKey: string | null;
    reopenCount: number;
    rank: string;
    residentPriority: string;
    residentPriorityMeaning: string | null;
    prefferedDay: string;
    prefferedTime: string;
    complaintLineId: number;
    zoneId: number;
    complaintLineNum: number;
    complaintLineStatus: string | null;
    complaintLineMeaning: string | null;
    lineCount: number | null;
    zoneName: string;
    idRef: string;
    closeOtp: string | null;
    showOtp: boolean;
    otpMismatch: boolean | null;
    allowEmail: string;
    residentEmailAddress: string;
    compositeComplaintNumber: string;
    complaintAssignEmpName: string;
    taskEmpName: string;
    compositeLocation: string;
    complaintNumber: number;
    jobCardNumber: number;
    specialStatus: string;
}

export interface ComplaintTaskHistVo {
    complaintId: number;
    complaint: Complaint;
    assignmentHist: Complaint[];
    taskList: Task[];
    taskHist: Task[];
    selectedTask: Task;
}

interface Complaint {
    complaintId: number;
    areaName: string;
    areaCode: string;
    complaintStatus: string;
    complaintDescription: string;
    complaintStatusMeaning: string;
    locationCode: string;
    qtrNumber: string;
    blockNumber: string;
    departmentId: number;
    departmentName: string;
    firstName: string;
    lastName: string;
    complaintDate: string; // Format: dd/MM/yyyy
    complaintMode: string;
    complaintModeMeaning: string;
    dueDate: string; // Format: dd/MM/yyyy
    residentMobile: string;
    comments: string;
    startDate: string; // Format: dd/MM/yyyy
    sevStatusMeaning: string;
    presentStatusDate: string; // Format: dd/MM/yyyy
    userNotes: string;
    locationName: string;
    smsMessage: string;
    reopenCount: number;
    handlingEmployeeId: number;
    handlingEmpFirstName: string;
    handlingEmpLastName: string;
    handlingEmpRank: string;
    complaintLineId: number;
    complaintLineNum: number;
    complaintLineStatus: string;
    complaintLineMeaning: string;
    lineCount: number;
    zoneId: number;
    zoneName: string;
    filedNo: number;
    residentRank: string;
    residentId: number;
    employeeRank: string;
    empMobileNumber: string;
    complaintCategoryId: number;
    parentCategoryId: number;
    categoryName: string;
    parentCategoryName: string;
    assignedEmployeeId: number;
    locationId: number;
    areaId: number;
    complaintPriority: number;
    complaintSeverty: number;
    categoryId: number;
    childCategoryName: string;
    employeeId: number;
    areadId: number; // Assuming this is a typo and should be "areaId"
    employeeMobile: string;
    residentEmail: string;
    employeeEmail: string;
    complaintComments: string;
}

interface Task {
    complaintId: number;
    taskId: number;
    complaintLineId: number;
    compSequenceNumber: number;
    totalComplaintCount: number;
    taskStatus: string;
    assEmpFName: string;
    assignEmpLName: string;
    assignDepartment: string;
    empMobileNumber: string;
    startDate: string; // Format: dd/MM/yyyy
    notes: string;
}
